/**
 * 슬라이드 생성 후 처리 유틸리티
 * DYNAMIC-LAYOUT-SPECIFICATION.md 규격에 따른 구현
 */

import { Slide, SlideElement, AspectRatio } from '../types';
import { SlideTheme } from '../components/SlideEditingPanel';
import { 
  generateRandomSlideSettings, 
  RandomSlideSettings,
  getRandomSplitPosition,
  getRandomDynamicLayout
} from './dynamicLayoutRandomizer';
import { 
  getContentArea, 
  getBackgroundArea, 
  getTitlePositionInBackground,
  calculateObjectPositions,
  countContentObjects
} from './dynamicLayout';
import { generateSlideHTML } from './slideGenerator';

/**
 * 제목 요소인지 판별
 * @param element 슬라이드 요소
 * @returns 제목 요소 여부
 */
const isTitleElement = (element: SlideElement): boolean => {
  // type이 'title'인 경우
  if (element.type === 'title') {
    return true;
  }
  
  // text 타입이 아니면 제목이 아님
  if (element.type !== 'text') { return false; }
  
  // textType이 명시적으로 'title'인 경우
  if ('textType' in element && element.textType === 'title') {
    return true;
  }
  
  // id에 'title'이 포함된 경우 (레거시 지원)
  if (element.id && element.id.includes('title')) {
    return true;
  }
  
  // 위치나 크기로 판단하지 않음 - 제목은 명시적으로만 판단
  return false;
};

/**
 * 슬라이드 테마 적용 (요소 위치 조정 포함)
 * @param slide 원본 슬라이드
 * @param slideTheme 적용할 슬라이드 테마
 * @param splitPosition 분할 위치
 * @param aspectRatio 화면 비율
 * @param includeTitleInLayout 제목을 레이아웃에 포함할지 여부
 * @returns 테마가 적용된 슬라이드
 */
const applySlideTheme = (
  slide: Slide, 
  slideTheme: SlideTheme, 
  splitPosition: number,
  aspectRatio: AspectRatio,
  includeTitleInLayout = false
): Slide => {
  const contentArea = getContentArea(aspectRatio, slideTheme, splitPosition);
  
  // 요소들을 제목/콘텐츠로 분리
  const titleElements: SlideElement[] = [];
  const contentElements: SlideElement[] = [];
  
  slide.elements?.forEach(element => {
    if (isTitleElement(element)) {
      titleElements.push(element);
    } else {
      contentElements.push(element);
    }
  });
  
  // 제목 요소 위치 조정
  const adjustedTitleElements = titleElements.map(element => {
    if (includeTitleInLayout) {
      // 제목을 콘텐츠 영역 내에 배치 (레이아웃에서 처리)
      return element;
    } else {
      // 제목을 배경 영역의 고정 위치에 배치
      const backgroundArea = getBackgroundArea(aspectRatio, slideTheme, splitPosition);
      const titlePosition = getTitlePositionInBackground(backgroundArea, slideTheme, aspectRatio);
      
      return {
        ...element,
        x: titlePosition.x,
        y: titlePosition.y,
        width: titlePosition.width,
        height: titlePosition.height,
        fontSize: titlePosition.fontSize
      };
    }
  });
  
  // 콘텐츠 요소들을 콘텐츠 영역 내로 제한
  const adjustedContentElements = contentElements.map(element => {
    const elementX = element.x < 1 ? element.x * aspectRatio.width : element.x;
    const elementY = element.y < 1 ? element.y * aspectRatio.height : element.y;
    const elementWidth = element.width < 1 ? element.width * aspectRatio.width : element.width;
    const elementHeight = element.height < 1 ? element.height * aspectRatio.height : element.height;
    
    // 콘텐츠 영역 내로 제한
    let newX = elementX;
    let newY = elementY;
    
    // X 좌표 제한
    if (elementX < contentArea.x) {
      newX = contentArea.x;
    } else if (elementX + elementWidth > contentArea.x + contentArea.width) {
      newX = Math.max(contentArea.x, contentArea.x + contentArea.width - elementWidth);
    }
    
    // Y 좌표 제한
    if (elementY < contentArea.y) {
      newY = contentArea.y;
    } else if (elementY + elementHeight > contentArea.y + contentArea.height) {
      newY = Math.max(contentArea.y, contentArea.y + contentArea.height - elementHeight);
    }
    
    // 비율 기반으로 다시 변환
    const finalX = element.x < 1 ? newX / aspectRatio.width : newX;
    const finalY = element.y < 1 ? newY / aspectRatio.height : newY;
    
    return {
      ...element,
      x: finalX,
      y: finalY
    };
  });
  
  return {
    ...slide,
    slideTheme,
    splitPosition,
    elements: [...adjustedTitleElements, ...adjustedContentElements]
  };
};

/**
 * 동적 레이아웃 적용
 * @param slide 슬라이드
 * @param randomSettings 랜덤 설정
 * @param aspectRatio 화면 비율
 * @param includeTitleInLayout 제목을 레이아웃에 포함할지 여부
 * @param layoutSpacing 레이아웃 간격
 * @returns 레이아웃이 적용된 슬라이드
 */
const applyDynamicLayout = (
  slide: Slide,
  randomSettings: RandomSlideSettings,
  aspectRatio: AspectRatio,
  includeTitleInLayout = false,
  layoutSpacing = 20
): Slide => {
  if (!randomSettings.dynamicLayout) {
    return slide;
  }
  
  const contentArea = getContentArea(aspectRatio, randomSettings.slideTheme, randomSettings.splitPosition);
  
  // 레이아웃에 포함할 요소들 필터링
  const elementsForLayout = slide.elements?.filter(element => {
    // 도형은 항상 제외
    if (element.type === 'shape') {return false;}
    
    // 제목 판별
    const isTitle = isTitleElement(element);
    
    // 제목 포함 여부에 따라 처리
    if (includeTitleInLayout) {
      // 제목도 포함: 도형만 제외
      return true;
    } else {
      // 제목 제외: 도형과 제목 모두 제외
      return !isTitle;
    }
  }) || [];
  
  if (elementsForLayout.length === 0) {
    return slide;
  }
  
  // 레이아웃 위치 계산
  const positions = calculateObjectPositions(
    randomSettings.dynamicLayout,  // LayoutOption (첫 번째 매개변수)
    elementsForLayout.length,       // objectCount (두 번째 매개변수)
    contentArea,                    // contentArea (세 번째 매개변수)
    aspectRatio.width,              // slideWidth (네 번째 매개변수)
    aspectRatio.height,             // slideHeight (다섯 번째 매개변수)
    layoutSpacing                   // spacing (여섯 번째 매개변수)
  );
  
  // 요소들에 새 위치 적용
  const updatedElements = slide.elements?.map(element => {
    const layoutIndex = elementsForLayout.findIndex(el => el.id === element.id);
    
    if (layoutIndex !== -1 && positions[layoutIndex]) {
      const position = positions[layoutIndex];
      return {
        ...element,
        x: position.x,  // 이미 비율 좌표
        y: position.y,
        width: position.width,
        height: position.height
      };
    }
    
    return element;
  }) || [];
  
  return {
    ...slide,
    elements: updatedElements,
    dynamicLayout: {
      selectedLayoutIndex: 0, // 임시값, 실제로는 레이아웃 옵션에서 찾아야 함
      availableLayouts: [randomSettings.dynamicLayout]
    }
  };
};

/**
 * 슬라이드 생성 후 자동 처리 (규격 1번)
 * 각 슬라이드별로 랜덤 테마 적용 → 안정화 → 랜덤 레이아웃 적용
 * @param slides 생성된 슬라이드들
 * @param aspectRatio 화면 비율
 * @param theme 현재 테마
 * @param themeFont 테마 폰트
 * @param slideBorderStyle 슬라이드 테두리 스타일
 * @param includeTitleInLayout 제목을 레이아웃에 포함할지 여부
 * @param layoutSpacing 레이아웃 간격
 * @returns 처리된 슬라이드들
 */
export const processGeneratedSlides = async (
  slides: Slide[],
  aspectRatio: AspectRatio,
  theme: { primary: string; secondary: string; accent: string; background: string },
  themeFont: { fontFamily: string; fontSize?: number; fontWeight?: string; effects?: Record<string, string> },
  slideBorderStyle: string,
  includeTitleInLayout = false,
  layoutSpacing = 0.02
): Promise<Slide[]> => {
  const processedSlides: Slide[] = [];
  
  for (const slide of slides) {
    console.log(`[슬라이드 후처리] 시작: ${slide.id}`);
    
    // 이미 slideTheme이 설정되어 있는지 확인
    const hasExistingTheme = slide.slideTheme && slide.slideTheme !== 'default';
    
    // 1. 콘텐츠 객체 수 계산 (제목 제외)
    const contentObjectCount = countContentObjects(
      slide.elements || [], 
      aspectRatio.height, 
      includeTitleInLayout
    );
    
    // 2. 랜덤 설정 생성 또는 기존 설정 사용
    let randomSettings: RandomSlideSettings;
    if (hasExistingTheme) {
      // 기존 테마가 있으면 그것을 사용
      randomSettings = {
        slideTheme: slide.slideTheme as SlideTheme,
        splitPosition: slide.splitPosition || getRandomSplitPosition(slide.slideTheme as SlideTheme),
        dynamicLayout: getRandomDynamicLayout(contentObjectCount)
      };
      console.log(`[슬라이드 후처리] 기존 테마 사용: 테마=${randomSettings.slideTheme}, 레이아웃=${randomSettings.dynamicLayout?.type}`);
    } else {
      // 기존 테마가 없으면 랜덤 생성
      randomSettings = generateRandomSlideSettings(contentObjectCount);
      console.log(`[슬라이드 후처리] 랜덤 설정: 테마=${randomSettings.slideTheme}, 레이아웃=${randomSettings.dynamicLayout?.type}`);
    }
    
    // 3. 슬라이드 테마 적용
    let processedSlide = applySlideTheme(
      slide, 
      randomSettings.slideTheme, 
      randomSettings.splitPosition,
      aspectRatio,
      includeTitleInLayout
    );
    
    // 4. 안정화 대기 (규격에 따른 await 처리)
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 5. 동적 레이아웃 적용
    if (randomSettings.dynamicLayout && slide.elements && slide.elements.length > 0) {
      processedSlide = applyDynamicLayout(
        processedSlide,
        randomSettings,
        aspectRatio,
        includeTitleInLayout,
        layoutSpacing
      );
      console.log(`[슬라이드 후처리] 동적 레이아웃 적용 완료: ${slide.id}`);
    } else {
      console.log(`[슬라이드 후처리] 동적 레이아웃 건너뜀: ${slide.id} (레이아웃 없음 또는 요소 없음)`);
    }
    
    // 6. 추가 안정화 대기
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // 7. HTML 재생성 (테마와 레이아웃이 적용된 상태로)
    const htmlContent = generateSlideHTML(
      processedSlide.title,
      processedSlide.content,
      theme,
      aspectRatio,
      processedSlide.order,
      themeFont,
      slideBorderStyle,
      processedSlide.slideLayout,
      processedSlide.backgroundImage,
      processedSlide.backgroundBlur,
      processedSlide.themeOverlay,
      processedSlide.backgroundColor,
      processedSlide.backgroundPattern,
      processedSlide.elements,
      'ppt', // 기본값
      processedSlide.fontBorder,
      processedSlide.fontGlow,
      randomSettings.slideTheme,
      processedSlide.dividerType || 'straight',
      randomSettings.splitPosition,
      processedSlide.contentBgColor
    );
    
    processedSlides.push({
      ...processedSlide,
      htmlContent
    });
  }
  
  console.log(`[슬라이드 후처리] 전체 완료: ${processedSlides.length}개 슬라이드`);
  return processedSlides;
};

/**
 * 슬라이드 테마 변경 후 레이아웃 재적용 (규격 2번)
 * @param slide 슬라이드
 * @param newTheme 새 테마
 * @param aspectRatio 화면 비율
 * @param existingLayout 기존 레이아웃 (유지해야 함)
 * @param includeTitleInLayout 제목 포함 여부
 * @param layoutSpacing 레이아웃 간격
 * @returns 처리된 슬라이드
 */
export const applyThemeChangeWithLayoutReapplication = async (
  slide: Slide,
  newTheme: SlideTheme,
  aspectRatio: AspectRatio,
  existingLayout: LayoutOption | null,
  includeTitleInLayout = false,
  layoutSpacing = 20
): Promise<Slide> => {
  console.log(`[테마 변경] 시작: ${slide.id}, 테마: ${newTheme}`);
  
  // 1. 테마 적용
  const themedSlide = applySlideTheme(
    slide,
    newTheme,
    slide.splitPosition || 30,
    aspectRatio,
    includeTitleInLayout
  );
  
  // 2. 안정화 대기
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // 3. 기존 레이아웃 재적용 (동일한 형태로)
  if (existingLayout) {
    // TODO: 기존 레이아웃 재적용 로직 구현
    console.log(`[테마 변경] 기존 레이아웃 재적용: ${existingLayout.type}`);
  }
  
  // 4. 추가 안정화
  await new Promise(resolve => setTimeout(resolve, 50));
  
  console.log(`[테마 변경] 완료: ${slide.id}`);
  return themedSlide;
};